import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ContextMenu from '../components/ContextMenu';

const Metadata = () => {
  const [metadata, setMetadata] = useState({});
  const [selectedType, setSelectedType] = useState('school');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formValue, setFormValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const metadataTypes = [
    { key: 'school', label: 'School/College' },
    { key: 'class', label: 'Class/Stream' },
    { key: 'subject', label: 'Subject' },
    { key: 'mode', label: 'Mode Preference' },
    { key: 'area', label: 'Area' },
    { key: 'location', label: 'Preferred Location' },
    { key: 'board', label: 'Board' }
  ];

  useEffect(() => {
    fetchMetadata();
  }, []);

  const fetchMetadata = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/metadata');
      setMetadata(response.data);
    } catch (error) {
      console.error('Error fetching metadata:', error);
      setMessage('Error fetching metadata');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      if (editingItem) {
        await axios.put(`/api/metadata/${editingItem._id}`, { value: formValue });
        setMessage('Item updated successfully!');
      } else {
        await axios.post('/api/metadata', { type: selectedType, value: formValue });
        setMessage('Item added successfully!');
      }

      setFormValue('');
      setEditingItem(null);
      fetchMetadata();
      setShowModal(false);
    } catch (error) {
      console.error('Error saving metadata:', error);
      setMessage(error.response?.data?.message || 'Error saving metadata');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormValue(item.value);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      await axios.delete(`/api/metadata/${id}`);
      setMessage('Item deleted successfully!');
      fetchMetadata();
    } catch (error) {
      console.error('Error deleting metadata:', error);
      setMessage('Error deleting metadata');
    }
  };

  const getCurrentTypeLabel = () => {
    return metadataTypes.find(t => t.key === selectedType)?.label || selectedType;
  };

  return (
    <div className="container" style={{ padding: '32px 24px' }}>
      <div className="page-header">
        <h1>Settings - Metadata Management</h1>
      </div>

      {message && (
        <div className={`alert ${message.includes('Error') ? 'alert-error' : 'alert-success'}`}>
          {message}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '250px 1fr', gap: '24px' }}>
        {/* Sidebar */}
        <div className="card" style={{ height: 'fit-content' }}>
          <h3>Categories</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {metadataTypes.map((type) => (
              <button
                key={type.key}
                className={`btn ${selectedType === type.key ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setSelectedType(type.key)}
                style={{ width: '100%', textAlign: 'left' }}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3>{getCurrentTypeLabel()}</h3>
            <button
              className="btn btn-primary"
              onClick={() => {
                setEditingItem(null);
                setFormValue('');
                setShowModal(true);
              }}
            >
              + Add {getCurrentTypeLabel()}
            </button>
          </div>

          {loading ? (
            <div className="loading">Loading...</div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Value</th>
                    <th>Created At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {!metadata[selectedType] || metadata[selectedType].length === 0 ? (
                    <tr>
                      <td colSpan="3" style={{ textAlign: 'center', padding: '40px' }}>
                        No items found. Click "Add {getCurrentTypeLabel()}" to create one.
                      </td>
                    </tr>
                  ) : (
                    metadata[selectedType].map((item) => (
                      <tr key={item._id}>
                        <td>{item.value}</td>
                        <td>{new Date(item.createdAt).toLocaleDateString()}</td>
                        <td className="actions">
                          <ContextMenu
                            onEdit={() => handleEdit(item)}
                            onDelete={() => handleDelete(item._id)}
                            itemName={item.value}
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <h2>{editingItem ? 'Edit' : 'Add'} {getCurrentTypeLabel()}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Value *</label>
                <input
                  type="text"
                  value={formValue}
                  onChange={(e) => setFormValue(e.target.value)}
                  placeholder={`Enter ${getCurrentTypeLabel()}`}
                  required
                  autoFocus
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Metadata;
